// src/components/DynamicSetupWizard/DynamicSetupWizard.tsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Spinner,
  Progress,
  Card,
  CardBody,
  Alert,
  Input,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { ThunkDispatch } from "@reduxjs/toolkit";
import type { Action } from "redux";

import type {
  Metadata,
  DiscoveryOpMeta,
  FieldMappingItem,
  Step,
  WidgetConfig,
} from "store/integrations/types";

import {
  getMetadataRequest,
  discoveryRequest,
  mappingCreateRequest,
} from "store/integrations/action";

import { propertyListGetRequest } from "store/properties/action";
import { ApplicationState } from "store";

import Stepper from "./Components/Stepper";
import CardPicker from "./Components/CardPicker";
import DropdownSelector from "./Components/DropdownSelector";
import FieldMapper from "./Components/FieldMapper";
import WizardNav from "./Components/WizardNav";
import VariableMapper, { MappingItem } from "./Components/VariableMapper";

type Option = { value: string; label: string };

export default function DynamicSetupWizard() {
  const { installationId } = useParams<{ installationId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

  // — Redux state —
  const tenantId = useSelector((s: ApplicationState) => s.auth.userProfile.tenant.id!);
  const metadata = useSelector((s: ApplicationState) => s.integration.metadata) as Metadata | undefined;
  const loadingMeta = useSelector((s: ApplicationState) => s.integration.loadingMetadata);
  const errorMeta = useSelector((s: ApplicationState) => s.integration.errorMetadata);
  const discovery = useSelector((s: ApplicationState) => s.integration.discovery);
  const loadingDisc = useSelector((s: ApplicationState) => s.integration.loadingDiscovery);
  const errorDisc = useSelector((s: ApplicationState) => s.integration.errorDiscovery);
  const properties = useSelector((s: ApplicationState) => s.properties.list);
  const loadingProps = useSelector((s: ApplicationState) => s.properties.loading);

  // — Local wizard state —
  const [stepIndex, setStepIndex] = useState(0);
  const [config, setConfig] = useState<Record<string, any>>({});
  const [pending, setPending] = useState<any>(null);
  const [mapping, setMapping] = useState<FieldMappingItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [modelSelection, setModelSelection] = useState<string[]>([]);
  const [varMapping, setVarMapping] = useState<MappingItem[]>([]);

  // 0) Steps and current-step memoization
  const steps = metadata?.setupSteps ?? [];

  const defaultStep = useMemo<Step & { widgets: WidgetConfig[] }>(
    () => ({
      id: "",
      type: "discovery",
      title: "",
      description: "",
      widgets: [],
      postAction: {},
    }),
    []
  );

  const current = useMemo<Step & { widgets: WidgetConfig[] }>(
    () => steps[stepIndex] ?? defaultStep,
    [steps, stepIndex, defaultStep]
  );

  // If metadata changes to fewer/more steps, reset out-of-bounds index
  useEffect(() => {
    if (stepIndex >= steps.length) {
      setStepIndex(0);
    }
  }, [steps, stepIndex]);

  // 1) load metadata once
  useEffect(() => {
    if (tenantId && installationId) {
      dispatch(getMetadataRequest(tenantId, installationId));
    }
  }, [dispatch, tenantId, installationId]);

  // 2) fire discoveryOps for every widget in this step
  useEffect(() => {
    console.log("🔍 discovery useEffect fired", {
      widgets: current.widgets,
      config,
      metadataLoaded: !!metadata
    });
    if (!metadata) return;
  
    for (const w of current.widgets) {
      const opName = w.discoveryOp;
      if (!opName) continue;
  
      const opMeta     = metadata.discovery[opName] as DiscoveryOpMeta;
      const paramsFrom = opMeta.paramsFrom ?? {};
      const q: Record<string,string> = {};
  
      for (const [param, sourceStep] of Object.entries(paramsFrom)) {
        const answer = config[sourceStep];
        if (answer == null) continue;
  
        const stepDef = metadata.setupSteps.find(s => s.id === sourceStep);
        let val: string | undefined;
  
        if (stepDef?.type === "form") {
          val = String(answer);
        } else {
          const srcMeta   = metadata.discovery[sourceStep] as DiscoveryOpMeta;
          const candidate = (answer as any)[param] ?? (answer as any)[srcMeta.idField];
          if (candidate != null) val = String(candidate);
        }
  
        if (val) q[param] = val;
      }
  
      console.log(`→ about to dispatch ${opName}`, q);
      dispatch(discoveryRequest(opName, tenantId, installationId!, q));
    }
  }, [
    dispatch,
    metadata,
    current.widgets,
    config,
    tenantId,
    installationId
  ]);
  


  // 3) seed mapping array exactly once when we hit a fieldMapper widget
  useEffect(() => {
    const fm = current.widgets.find(w => w.widget === "fieldMapper");
    if (!fm?.discoveryOp) return;
    if (mapping.length) return; // already seeded
    const discFM = (discovery[fm.discoveryOp!] || { records: [] }) as { records: any[]; template?: any };
    const arr = discFM.records;
    const op = metadata!.discovery[fm.discoveryOp] as DiscoveryOpMeta;
    setMapping(arr.map(item => ({
      form_field: { key: item[op.idField], label: item[op.labelField]! },
      crm_field: null
    })));
  }, [discovery, current, metadata, mapping.length]);

  useEffect(() => {
    const vm = current.widgets.find(w => w.widget === "variableMapper");
    if (!vm || varMapping.length) return;

    // ← UPDATE: always grab .records, defaulting to empty array
    const discVM = discovery[vm.discoveryOp!] as { records: any[]; template?: any } | undefined;
    const list = discVM?.records ?? [];

    const op = metadata!.discovery[vm.discoveryOp!] as DiscoveryOpMeta;
    const placeholders = list.map(rec => `{{${rec[op.idField!]}}}`);

    setVarMapping(placeholders.map(ph => ({ placeholder: ph, mappedTo: null })));

    // default each row’s modelSelection to the **first** option
    if (vm.mappingSource?.type === "multiModel") {
      const firstKey = vm.mappingSource.options[0].modelKey;
      setModelSelection(placeholders.map(() => firstKey));
    }
  }, [current, discovery, metadata, varMapping.length]);

  useEffect(() => {
    const vm = current.widgets.find(w => w.widget === "variableMapper");
    if (vm?.mappingSource?.type === "multiModel" && vm.mappingSource.options.length) {
      const firstKey = vm.mappingSource.options[0].modelKey;
      dispatch(propertyListGetRequest(firstKey, { pageSize: 1000 }, 0));
    }
  }, [dispatch, current.widgets]);

  // 4) load CRM fields if this step’s mapper is model-backed
  useEffect(() => {
    const fm = current.widgets.find(
      (w): w is WidgetConfig & { mappingSource: { type: "model"; modelKey: string } } =>
        w.widget === "fieldMapper" && w.mappingSource?.type === "model"
    );
    if (fm) {
      dispatch(propertyListGetRequest(fm.mappingSource.modelKey, { pageSize: 1000 }, 0));
    }
  }, [dispatch, current]);

  // build CRM-field dropdown options
  const fieldOptions: Option[] = useMemo(
    () => properties.map((p: { valuesJson: any; }) => {
      const def = JSON.parse(p.valuesJson || "{}");
      return { value: def.key, label: def.label };
    }),
    [properties]
  );

  // — Navigation callbacks —
  const handleNext = useCallback(() => {
    const id = current.id;
    if (current.widgets.some(w => w.widget === "textInput")) {
          // no-op, config[field] was set in onChange
    } else if (current.widgets.some(w => w.widget === "variableMapper")) {
      // find the VM widget so we know which discoveryOp to pull
      const vm = current.widgets.find(w => w.widget === "variableMapper")!;
      // grab its discovery result (records + template)
      const discVM = (discovery[vm.discoveryOp!] as { records: any[]; template?: any }) || {};
      setConfig(c => ({
        ...c,
        [id]: {
          // your field‐to‐CRM mapping array
          mapping: varMapping,
          // the labelField for this op is "elementName", so grab it
          elementName: discVM.template?.elementName ?? "",
          // if you want to ship the whole template object along:
          rawTemplate: discVM.template
        }
      }));
    } else if (current.widgets.some(w => w.widget === "fieldMapper")) {
      setConfig(c => ({ ...c, [id]: mapping }));
    } else {
      setConfig(c => ({ ...c, [id]: pending }));
    }
    setPending(null);
    setStepIndex(i => i + 1);
  }, [current, mapping, pending]);

  const handlePrev = () => setStepIndex(i => i - 1);

  const handleFinish = () => {
    setSaving(true)
    setSaveErr(null)

    // 1) assemble finalConfig, handling variableMapper as well
    const id = current.id
    let finalConfig: Record<string, any>
    if (current.widgets.some(w => w.widget === "variableMapper")) {
      // pull the same bits you did in handleNext:
      const vm = current.widgets.find(w => w.widget === "variableMapper")!
      const discVM = (discovery[vm.discoveryOp!] as { records: any[]; template?: any }) || {}
      finalConfig = {
        ...config,
        [id]: {
          mapping: varMapping,
          elementName: discVM.template?.elementName ?? "",
          rawTemplate: discVM.template
        }
      }
    } else if (current.widgets.some(w => w.widget === "fieldMapper")) {
      finalConfig = { ...config, [id]: mapping }
    } else {
      finalConfig = { ...config, [id]: pending }
    }

    // 2) choose displayName—either from nameFromStep or fallback
    let displayName: string
    const pa = current.postAction || {}

    if (pa.nameFromStep) {
      const stepId = pa.nameFromStep
      const answer = (finalConfig as any)[stepId]
      const opMeta = metadata!.discovery[stepId] as DiscoveryOpMeta
      const labelKey = opMeta.labelField!

      displayName =
        Array.isArray(answer)
          ? (answer as any[]).map(a => a[labelKey]).join(", ")
          : String(answer[labelKey])
    }
    else {
      displayName = finalConfig.setupName
        || finalConfig.name
        || "New Setup"
    }

    // 3) dispatch with our new displayName
    dispatch(mappingCreateRequest(
      tenantId,
      installationId!,
      displayName,
      finalConfig,
      () => navigate("/integrations"),
      (err: any) => {
        setSaving(false)
        setSaveErr(err.message || "Save failed")
      }
    ))
  }

  // — Guards & loaders —
  if (loadingMeta) {
    return (
      <div className="d-flex flex-column gap-4">
        {/* … your existing skeleton UI … */}
      </div>
    );
  }
  if (errorMeta) {
    return <Alert color="danger" className="m-5 text-center">
      Failed to load setup: {errorMeta}
    </Alert>;
  }
  if (!metadata || !steps.length) {
    return <div className="text-center py-5 text-muted">No setup required.</div>;
  }

  // — render the right widget for each entry in current.widgets —
  const renderWidget = (w: WidgetConfig) => {
    const disc = (discovery[w.discoveryOp!] || { records: [], template: undefined }) as { records: any[]; template?: any };
    switch (w.widget) {
      case "cardPicker":
        return (
          <CardPicker
            key={w.discoveryOp}
            title={current.title}
            description={current.description}
            items={disc.records}
            idField={w.idField!}
            labelField={w.labelField!}
            logoField={w.logoField}
            loading={loadingDisc[w.discoveryOp!]}
            error={errorDisc[w.discoveryOp!]}
            pending={pending}
            onSelect={setPending}
          />
        );
      case "dropdown":
        return (
          <DropdownSelector
            key={w.discoveryOp}
            title={current.title}
            description={current.description}
            items={disc.records}
            idField={w.idField!}
            labelField={w.labelField!}
            loading={loadingDisc[w.discoveryOp!]}
            error={errorDisc[w.discoveryOp!]}
            pending={pending}
            onSelect={setPending}
          />
        );
      case "fieldMapper":
        return (
          <FieldMapper
            key="fieldMapper"
            title={current.title}
            description={current.description}
            mapping={mapping}
            fieldOptions={fieldOptions}
            loading={loadingProps}
            error={saveErr}
            onChange={(i, opt) => {
              const next = [...mapping];
              next[i].crm_field = opt ? { key: opt.value, label: opt.label } : null;
              setMapping(next);
            }}
          />
        );
      case "variableMapper": {
        const vm = w as WidgetConfig & { mappingSource: any };

        // ← UPDATE: pull records safely
        const discVM = discovery[vm.discoveryOp!] as { records: any[]; template?: any } | undefined;
        const items = discVM?.records ?? [];

        const opMeta = metadata!.discovery[vm.discoveryOp!] as DiscoveryOpMeta;
        const placeholders = items.map(r => `{{${r[opMeta.idField]}}}`);

        return (
          <VariableMapper
            key={vm.discoveryOp}
            title={current.title}
            description={current.description}
            placeholders={placeholders}
            mapping={varMapping}
            onChange={setVarMapping}
            onModelSelect={(modelKey) =>
              dispatch(propertyListGetRequest(modelKey, { pageSize: 1000 }, 0))
            }
            mappingSource={vm.mappingSource!}
            properties={properties}       // your redux properties list
            allowCustom={!!vm.allowCustom}
            loading={loadingDisc[vm.discoveryOp!]}
            loadingOptions={loadingProps}
            error={errorDisc[vm.discoveryOp!] ?? null}
            template={vm.renderTemplate?.fromDiscovery ? discVM?.template : undefined}
          />
        );
      }

      case "textInput": {
        // 1) make sure we actually have an idField
        if (!w.idField) {
          console.error("textInput widget missing .idField");
          return null;
        }
        const field = w.idField;
      
        // 2) decide what text to show above the input
        const labelText = w.label ?? w.placeholder ?? field;
      
        return (
          <div key={field} className="mb-4">
            {/* step header & description */}
            <h5 className="fw-bold">{current.title}</h5>
            <p className="text-secondary mb-4">{current.description}</p>
      
            {/* the form group */}
            <div className="mb-3">
              <label htmlFor={field} className="form-label">
                {labelText}
              </label>
              <Input
                type="text"
                id={field}
                value={config[field] ?? ""}
                placeholder={w.placeholder}
                onChange={e =>
                  setConfig(c => ({ ...c, [field]: e.target.value }))
                }
              />
            </div>
          </div>
        );
      }
      
      default:
        return <Alert color="warning" key={w.widget}>
          Unknown widget “{w.widget}”
        </Alert>;
    }
  };

  return (
    <div className="d-flex flex-column gap-4">
      <Stepper steps={steps} currentIndex={stepIndex} />

      <Card className="shadow-sm rounded-lg mb-4">
        <CardBody>
          <Progress
            value={(stepIndex / (steps.length - 1)) * 100}
            className="rounded-pill mb-4"
          />

          {current.widgets.map(renderWidget)}

          <WizardNav
            stepIndex={stepIndex}
            steps={steps}
            pending={pending}
            currentType={current.type}
            saving={saving}
            onPrev={handlePrev}
            onNext={handleNext}
            onFinish={handleFinish}
            finishEnabled={
              current.widgets.some(w => w.widget === "fieldMapper")
                ? mapping.filter(m => m.crm_field).length >= (current.postAction?.minItemsRequired || 0)
                : true
            }
          />
        </CardBody>
      </Card>
    </div>
  );
}
